import React, { useCallback, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import {
  AppButton,
  Box,
  FlexControlledForm,
  Preloader,
} from '@common-fe/common-fe';
import styled from 'styled-components';
import * as yup from 'yup';

import colors from '@/styles/colors';

import { Dependent } from '../../employee.types';

import { useCreateDependentQuery, useUpdateDependent } from './hooks/useDependents.query';
import { useAddDependentFields } from './hooks';

const BUTTON_WIDTH = '140px';

const StyledFormBox = styled(Box)`
  input[data-testid$='_ssn_mask-text-input'] {
    font-feature-settings: 'calt' 0;
  }
`;

interface Props {
  onCancel?: () => void;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  onSuccess?: (response?: any) => void;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  onError?: (response?: any) => void;
  dependent?: Dependent;
}

export const AddDependentForm: React.FC<Props> = ({ onCancel, onSuccess, onError, dependent }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { params: { id: employeeId } } = useRouteMatch<{ id: string }>();
  const [formValues, setFormValues] = useState({});
  const [activeValues, setActiveValues] = useState(false);
  const [isSubmit, setSubmit] = useState(false);

  const { createDependent } = useCreateDependentQuery({ employeeId, onSuccess, onError });
  const { updateDependent } = useUpdateDependent({ onSuccess, onError });
  const fields = useAddDependentFields(dependent);

  const handleChangeValues = useCallback((values: object) => {
    setFormValues((prev) => ({ ...prev, ...values }));
    try {
      const validatorMap = fields.reduce((map, field) => ({
        ...map,
        [field.name]: field.validator,
      }), {});
      const schema = yup.object()
        .shape(validatorMap);
      schema.validateSync(values, { abortEarly: false });
      setActiveValues(true);
    } catch {
      setActiveValues(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = useCallback(async () => {
    setSubmit(true);
    if (activeValues && !dependent) {
      setIsLoading(true);
      try {
        await createDependent(formValues as Dependent);
      } catch {
        setIsLoading(false);
      }
    }

    if (activeValues && dependent) {
      setIsLoading(true);
      try {
        await updateDependent({
          ...formValues, 
          dependentId: dependent?.dependentId,
          employeeId: dependent?.employeeId || employeeId,
        } as Dependent);
      } catch {
        setIsLoading(false);
      }
    }
  }, [setIsLoading, activeValues, createDependent, formValues, updateDependent, dependent, employeeId]);

  return (
    <>
      <StyledFormBox
        pad="spacing24"
        round="moduleRound"
        background={{ color: colors.module }}
      >
        <FlexControlledForm
          fields={fields}
          editMode
          testId="add-dependent-form"
          showError={isSubmit}
          onChangeValues={handleChangeValues}
          isModalType
          wrapperStyle={{ background: colors.canvas, border: `1px solid ${colors.border2}` }}
        />
      </StyledFormBox>
      <Box direction="row" justify="end" align="center" pad={{ top: 'medium' }} gap="spacing12">
        <AppButton
          testId="add-dependent-form_cancel"
          buttonType="secondary"
          onClick={onCancel}
          width={BUTTON_WIDTH}
        >
          Cancel
        </AppButton>
        <AppButton
          testId="add-dependent-form_submit"
          onClick={handleSave}
          width={BUTTON_WIDTH}
          disabled={isLoading}
        >
          {isLoading
            ? <Preloader testId="add-dependent-form_submit_loading" color={colors.iconOnColor} />
            : (dependent ? 'Save' : 'Add Dependent')}
        </AppButton>
      </Box>
    </>
  );
};
